import dompurify from "dompurify";
import parser from "html-react-parser";
import React, { useEffect, useState } from "react";
import { ButtonGroup, Card, Form, Image, Table } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { useChoiceLabel } from "@/const";
import { useRequest } from "@components/AppHook";
import VkVideoPlayer from "@components/Common/VkVideoPlayer";
import { DevForm, FormFieldGroup } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { RenderForm } from "@components/Forms/Render";
import { GameUnpublishRelationship } from "@components/Game/Misc";
import { DefaultLayout } from "@components/Layouts";
import { ListingCard } from "@components/Pages";
import { CountriesBareForm } from "@components/Screen/CountryBlocks";
import { url_reverse } from "@components/Utils";

const GameLink = ({ game }) => {
    return game && <Link to={url_reverse("games:admin", { game_id: game.id })}>{game.__model_locale_object.name}</Link>;
};

export default function GameModerationScreen() {
    const params = useParams();

    return <DefaultLayout>{params.id ? <Edit id={params.id} /> : <List />}</DefaultLayout>;
}

const List = () => {
    const { t } = useTranslation();
    const { apiData } = useAppApi(url_reverse("api:moderation:game:list"));

    if (!apiData) return;

    return (
        <ListingCard header={t("Игры (постмодерация)")}>
            <Table hover responsive className="dmg-table">
                <thead>
                    <tr>
                        <th>{t("Игра")}</th>
                    </tr>
                </thead>
                <tbody>
                    {apiData.object_list?.map((game) => (
                        <tr key={game.id}>
                            <td>
                                <Link to={url_reverse("admin:moderation:game:edit", { game_id: game.id })}>
                                    {game.__model_locale_object.name}
                                </Link>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </ListingCard>
    );
};

const Edit = ({ id }) => {
    const { apiData } = useAppApi(url_reverse("api:moderation:game:edit", { game_id: id }));
    const navigate = useNavigate();

    useEffect(() => {
        let redirect = true;
        if (apiData?.status) {
            if (apiData.forms_data?.unpublish_moder) {
                redirect = false;
            }
            if (apiData.forms_data?.country_moder) {
                redirect = false;
            }
            if (apiData.locks) {
                redirect = false;
            }
            if (apiData.transfer_game) {
                redirect = false;
            }
            if (redirect) {
                navigate(url_reverse("admin:moderation:game:list"));
            }
        }
    }, [apiData]);

    if (!apiData) return;

    return (
        <>
            <ModerationDraftLocks />
            <ModerationCountries />
            <ModerationUnpublish />
            <ModerationTransfer />
        </>
    );
};

const ModerationTransfer = () => {
    const { t } = useTranslation();
    const { apiData, apiUrl, setApiData } = useAppContext();
    const { api_post } = useRequest();
    const DeveloperResidenceTerr = useChoiceLabel("DeveloperResidenceTerr");

    return (
        apiData.transfer_game && (
            <Card className="mb-3">
                <Card.Header>
                    {t("Передача проекта")} &mdash; <GameLink game={apiData.game} />
                </Card.Header>
                <Card.Body>
                    <p>
                        <Trans time_transfer={apiData.transfer.time_transfer}>
                            У игры сменился правообладатель с "{{ time_transfer: apiData.transfer.time_transfer }}".
                            Внесите необходимые изменения в сервисе MyTracker и нажмите кнопку "Готово"
                        </Trans>
                    </p>
                    <Table bordered>
                        <tbody>
                            <tr>
                                <th>
                                    {t("Компания")} {t("(старое/новое)")}
                                </th>
                                <td>
                                    <Link to={url_reverse("devs:edit", { dev_id: apiData.transfer.dev_transfer_id })}>
                                        {apiData.transfer.dev_transfer.name}
                                    </Link>{" "}
                                    /{" "}
                                    <Link to={url_reverse("devs:edit", { dev_id: apiData.transfer.dev_holder_id })}>
                                        {apiData.transfer.dev_holder.name}
                                    </Link>
                                </td>
                            </tr>
                            <tr>
                                <th>
                                    {t("Территории резидентства")} {t("(старое/новое)")}
                                </th>
                                <td>
                                    {DeveloperResidenceTerr[apiData.transfer.dev_transfer.residence_terr]} /{" "}
                                    {DeveloperResidenceTerr[apiData.transfer.dev_holder.residence_terr]}
                                </td>
                            </tr>
                            <tr>
                                <th>
                                    {t("Юридическое наименование")} {t("(старое/новое)")}
                                </th>
                                <td>
                                    {apiData.transfer.dev_transfer.company_ru} /{" "}
                                    {apiData.transfer.dev_holder.company_ru}
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                    <DevForm.Button
                        variant="success"
                        name="action"
                        value="transfer_done"
                        onClick={(ev) => {
                            api_post(ev, apiUrl, {
                                params: {
                                    [ev.target.name]: ev.target.value,
                                    transfer_id: apiData.transfer.id,
                                    game_id: apiData.game.id,
                                },
                                then: (resp) => {
                                    setApiData(resp);
                                },
                            });
                        }}
                    >
                        {t("Готово")}
                    </DevForm.Button>
                </Card.Body>
            </Card>
        )
    );
};

const ModerationUnpublish = () => {
    const { t } = useTranslation();
    const { apiData } = useAppContext();
    const onFormSubmit = useSubmit();

    return (
        apiData.unpublish_rel && (
            <Card className="mb-3">
                <Card.Header>
                    {t("Снять с публикации")} &mdash; <GameLink game={apiData.game} />
                </Card.Header>
                <Card.Body>
                    <h6>{t("Комментарий разработчика")}</h6>
                    <div className="font-weight-light mb-3 show-wrap-text">{apiData.game.developer_comment}</div>
                    <GameUnpublishRelationship relationship={apiData.unpublish_rel} />
                    <Form onSubmit={onFormSubmit}>
                        <RenderForm prefix="unpublish_moder" bareForm={true} asRow={false} />
                        <ButtonGroup>
                            <DevForm.Button type="submit" variant="success" name="action" value="unpublish_approve">
                                {t("Принять")}
                            </DevForm.Button>{" "}
                            <DevForm.Button type="submit" variant="danger" name="action" value="unpublish_reject">
                                {t("Отклонить")}
                            </DevForm.Button>
                        </ButtonGroup>
                    </Form>
                </Card.Body>
            </Card>
        )
    );
};

const ModerationDraftLocks = () => {
    const { t } = useTranslation();
    const { apiData, setApiData, apiUrl } = useAppContext();
    const [data, setData] = useState({});
    const { api_post } = useRequest();

    useEffect(() => {
        if (apiData?.locks) {
            const newData = {};
            apiData.locks?.forEach((item) => {
                const diffFields = [];
                item.__moderation_diff.forEach((diff) => {
                    diffFields.push(diff.name);
                });
                newData[item.id] = {
                    refused: [],
                    approved: diffFields,
                    comment: "",
                    state: "accepted",
                    checked: [],
                    allFields: diffFields,
                };
            });
            setData(newData);
        }
    }, [apiData]);

    const handleComment = (id, value) => {
        const d = { ...data };
        d[id].comment = value;
        setData(d);
    };

    const handleCheckbox = (id, name, check) => {
        const d = { ...data };
        if (check) {
            if (!d[id].refused.includes(name)) {
                d[id].refused.push(name);
            }
            d[id].state = "refused";
            d[id].approved = d[id].approved.filter((item) => item !== name);
        } else {
            if (!d[id].approved.includes(name)) {
                d[id].approved.push(name);
            }
            d[id].refused = d[id].refused.filter((item) => item !== name);
        }
        d[id].checked = d[id].refused;

        if (d[id].checked.length === 0) {
            d[id].state = "accepted";
        }

        setData(d);
    };

    const handleToggleAll = (e, obj) => {
        const d = { ...data };

        if (e.target.checked) {
            d[obj.id].checked = d[obj.id].allFields;
            d[obj.id].allFields.forEach((diff) => {
                handleCheckbox(obj.id, diff, true);
            });
        } else {
            d[obj.id].checked = [];
            d[obj.id].allFields.forEach((diff) => {
                handleCheckbox(obj.id, diff, false);
            });
        }
    };

    const handleSubmit = (ev) => {
        const id = ev.target.value;
        const params = {
            id,
            comment: data[id].comment,
            data: JSON.stringify({
                approved: data[id].approved,
                refused: data[id].refused,
            }),
            state: data[id].state,
        };
        api_post(ev, apiUrl, {
            params,
            then: (resp) => {
                setApiData(resp);
            },
        });
    };

    return (
        apiData.locks &&
        apiData.locks.map((obj) => (
            <Card key={obj.id} className="mb-3">
                <Card.Header>
                    {obj.__draft_name} {obj.locale && <b>{obj.locale}</b>} от {obj.author.login} [{obj.date_added}]
                    &mdash; <GameLink game={apiData.game} />
                </Card.Header>
                <Card.Body>
                    <div className="table-responsive">
                        <table className="table table-bordered">
                            <thead>
                                <tr>
                                    <th>
                                        <DevForm.Checkbox
                                            name="toggle_all"
                                            onChange={(e) => handleToggleAll(e, obj)}
                                            checked={data[obj.id]?.checked.length === data[obj.id]?.allFields.length}
                                        />
                                    </th>
                                    <th width="19%">{t("Поле")}</th>
                                    <th width="39%">{t("Старое значение")}</th>
                                    <th width="39%">{t("Новое значение")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {obj.__moderation_diff.map((d, i) => (
                                    <tr key={`diff-key-${i}`}>
                                        <td>
                                            <DevForm.Checkbox
                                                name={d.name}
                                                onChange={(e) => handleCheckbox(obj.id, d.name, e.target.checked)}
                                                checked={data[obj.id]?.checked.includes(d.name) || false}
                                            />
                                        </td>
                                        <td>
                                            <div>{d.title}</div>
                                            <div className="badge badge-secondary">{d.name}</div>
                                        </td>
                                        <td style={{ maxWidth: "600px", overflowX: "auto" }}>
                                            <RenderValue val={d.old} fieldType={d.field_type} fieldName={d.name} />
                                        </td>
                                        <td style={{ maxWidth: "600px", overflowX: "auto" }}>
                                            <RenderValue
                                                val={d.new}
                                                fieldType={d.field_type}
                                                fieldName={d.name}
                                                green={true}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <FormFieldGroup label={t("Комментарий")} asRow={false}>
                        <DevForm.TextArea
                            name="comment"
                            value={data[obj.id]?.comment}
                            onChange={(e) => handleComment(obj.id, e.target.value)}
                        />
                    </FormFieldGroup>
                    <ButtonGroup>
                        <DevForm.Button
                            variant="success"
                            name="accept"
                            value={obj.id}
                            disabled={data[obj.id]?.state === "refused"}
                            onClick={handleSubmit}
                        >
                            {t("Принять")}
                        </DevForm.Button>
                        <DevForm.Button
                            variant="danger"
                            name="refuse"
                            value={obj.id}
                            disabled={data[obj.id]?.state === "accepted"}
                            onClick={handleSubmit}
                        >
                            {t("Отклонить")}
                        </DevForm.Button>
                    </ButtonGroup>
                </Card.Body>
            </Card>
        ))
    );
};

const ModerationCountries = () => {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppContext();
    const onFormSubmit = useSubmit();

    return (
        apiData.forms_data?.countries && (
            <Card className="mb-3">
                <Card.Header>
                    {t("Страны")} &mdash; <GameLink game={apiData.game} />
                </Card.Header>
                <Card.Body>
                    <div className="mb-3">
                        <CountriesBareForm
                            prefix="countries"
                            apiData={apiData}
                            setApiData={setApiData}
                            makeDiff={apiData.country_diff}
                        />
                    </div>
                    <Form onSubmit={onFormSubmit}>
                        <RenderForm prefix="country_moder" bareForm={true} asRow={false} />
                        <div className="mt-3">
                            <ButtonGroup>
                                <DevForm.Button type="submit" variant="success" name="action" value="countries_approve">
                                    {t("Принять")}
                                </DevForm.Button>
                                <DevForm.Button type="submit" variant="danger" name="action" value="countries_reject">
                                    {t("Отклонить")}
                                </DevForm.Button>
                            </ButtonGroup>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        )
    );
};

const VKVideoPresenter = ({ video_id }) => {
    const { t } = useTranslation();
    const [data, setData] = useState();
    const { api_post } = useRequest();

    const fetchData = (ev) => {
        api_post(ev, url_reverse("api:get_vkvideo_data"), {
            params: { video_id },
            then: (resp) => setData(resp.data),
        });
    };

    return (
        <>
            {!data && (
                <DevForm.Button size="sm" name="show_video" onClick={fetchData}>
                    {t("Показать видео")}
                </DevForm.Button>
            )}

            {data && <VkVideoPlayer data={data} height="300" />}
        </>
    );
};

const RenderValue = ({ val, fieldType, fieldName, green = false }) => {
    const text_class = green ? "text-success" : "text-danger";
    const ftype = fieldType.match(/<class '.*\.([^.]+)'>/)[1];
    if (fieldName === "vkvideo_id" && val) {
        return (
            <>
                <div className={text_class}>{val}</div>
                <VKVideoPresenter video_id={val} />
            </>
        );
    } else if ((ftype === "HotboxImageField" || ftype === "HotboxIconField") && val && val.indexOf("/hotbox/") === 0) {
        return (
            <a href={val} target="_blank">
                <Image src={val} style={{ maxHeight: "150px", backgroundColor: "black", padding: "0px" }} />
            </a>
        );
    } else if (ftype === "HotboxVideoField" && val && val.indexOf("/hotbox/") === 0) {
        return (
            <>
                <a href={val} target="_blank">
                    {val}
                </a>
                <br />
                <video src={val} controls style={{ maxHeight: "150px" }} />
            </>
        );
    } else if (ftype === "URLField") {
        return (
            <a href={val} target="_blank">
                {val}
            </a>
        );
    } else if (typeof val === "object") {
        if (val === null) {
            return <div className={text_class}>None</div>;
        }
        return <pre className={text_class}>{JSON.stringify(val, null, 2)}</pre>;
    }
    return <div className={text_class}>{parser(dompurify.sanitize(val || "None", { ADD_ATTR: ["target"] }))}</div>;
};
